import RecapResponse from '../model/RecapResponse';
import { normalizeIdentifier } from '../util';
import m from './module';
import './RecapDefinitionService';
import { createError, getApiError } from '../error';
import { INCREMENTAL_PURGE_OLD_DATA_STORE } from "../constants";

const getRecapResponseSetUrl = '/recap_response_set';
const getMyRecapResponseSetsUrl = '/recap_response_set/staffing_user/{{staffing_users_id}}';
const uploadRecapResponseFile = '/recap_response_set/upload';
const type = 'RecapResponse';

class RecapResponseService {
  constructor(
    $log,
    $q,
    $injector,
    RecapDefinitionService,
    EventService,
    APIService,
    DataSyncService,
    IncrementalDataStoreService,
    LogService,
    DateTimeService,
    DocumentService,
    FormResponseService,
    FormResponseSetService,
    FieldResponseService,
    LocationVariableService,
    AppVersionService,
    FormService
  ) {
    "ngInject";
    this.$log = $log;
    this.$q = $q;
    this.$injector = $injector;
    this.APIService = APIService;
    this.RecapDefinitionService = RecapDefinitionService;
    this.EventService = EventService;
    this.DataSyncService = DataSyncService;
    this.IncrementalDataStoreService = IncrementalDataStoreService;
    this.LogService = LogService;
    this.DateTimeService = DateTimeService;
    this.FormResponseService = FormResponseService;
    this.FormResponseSetService = FormResponseSetService;
    this.FieldResponseService = FieldResponseService;
    this.LocationVariableService = LocationVariableService;
    this.FormService             = FormService;
    this.AppVersionService       = AppVersionService;
    this.DocumentService         = DocumentService;

    this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName = {};
    this._mostRecentDataStore = null;

    // Track what paths have been used for get multiple objects.
    // This is an ugly hack so we can later override a recap response set.
    this._usedPathsForGetMultiple = [];

    const expireIncrementalDataHandler = () => {
      return this.$q.when(this._mostRecentDataStore)
        .then(dataStore => {
          if (dataStore && dataStore.setNeedsUpdate) {
            dataStore.setNeedsUpdate();
          }
        });
    };
    // note: added "pre-refresh" hooks while debugging MOSAICPT-2127
    // not sure why we had it set up like this previously.. every time we do a sync, we would mark the data as stale
    // and cause it to require being re-downloaded
    this.DataSyncService.addPreRefreshPositionsCallback(expireIncrementalDataHandler);
    this.DataSyncService.addPreRefreshDataCallback(expireIncrementalDataHandler);
  }

  get StaffUserService() {
    return this.$injector && this.$injector.get('StaffUserService');
  }

  get PositionService() {
    return this.$injector && this.$injector.get('PositionService');
  }

  create() {
    const data = arguments[0] || {};
    const config = arguments[1] || null;
    const promises = [];
    const flashMessages = data.flash_messages

    data.id = data.id || null;
    const recapResponseId = data.id;

    let recapDefinitionPromise;
    // Route both branches through RecapDefinitionService so the definition's
    // forms / pages get registered with FormService (DataSyncService cache).
    // The previous "if data.recap_definition" branch wrapped the raw payload
    // in $q.when without processing, which meant FormService.getById(formId)
    // could return falsy for forms that hadn't yet been registered. That
    // silently dropped the corresponding form_response_set (loadFormResponseSet
    // rejects with 'MissingForm' and the outer .catch swallows it), producing
    // a partial recap response - notably missing product-form responses on
    // first load after a merchandising recap submission.
    const recapDefinitionId = (data.recap_definition && data.recap_definition.id)
      || data.events_recaps_id
      || null;
    if (!recapDefinitionId) {
      return this.$q.reject(createError({
        error: 'NoRecapDefinition',
        message: 'Recap Definition is required to create Recap Response Set',
      }));
    }
    if (data.recap_definition && typeof data.recap_definition.findAllValidations === 'function') {
      // Already an instantiated RecapDefinition model (forms registered).
      recapDefinitionPromise = this.$q.when(data.recap_definition);
    } else if (data.recap_definition) {
      // Raw payload - process via the service so forms get registered.
      recapDefinitionPromise = this.$q.when(this.RecapDefinitionService.create(data.recap_definition))
        .then(recapDefinition => {
          data.recap_definition = recapDefinition;
          return recapDefinition;
        });
    } else {
      recapDefinitionPromise = this.RecapDefinitionService.getById(recapDefinitionId, config)
        .then(recapDefinition => {
          data.recap_definition = recapDefinition;
          return recapDefinition;
        });
    }

    const previewLocationVariableIds = data.previewLocationVariableIds || [];
    promises.push(
      this.LocationVariableService.getByIds(previewLocationVariableIds)
        .then(locationVariables => {
          this.LogService.debug(`RecapResponseService.create() created preview location variables:`, locationVariables);
          data.preview_location_variables = locationVariables;
        })
    );

    let eventPromise;
    if (data.events_id) {
      this.LogService.debug(`RecapResponseService.create() calling EventService.getById() events_id=${data.events_id}`);
      eventPromise = this.EventService.getById(data.events_id)
    } else {
      eventPromise = this.$q.when(null);
    }
    eventPromise = eventPromise
      .then(event => {
        this.LogService.debug(`RecapResponseService.create() resolved eventPromise; setting data.event to event. id=${event && event.id}`);
        data.event = event;
      });

    const initialPromises = [
      recapDefinitionPromise,
      eventPromise,
    ];

    let event = null;
    let eventTimezoneName = null;

    let user_role_id = data.role_variable_id;
    let current_location = null;

    promises.push(
      this.$q.all(initialPromises)
        .then(() => {
          // Retrieve form for each form response set
          event = data.event || null;

          if (!user_role_id) {
            user_role_id = data.recap_definition && data.recap_definition.user_role_id;
          }
          current_location = data.recap_definition && data.recap_definition.current_location;

          // Set recap specific timezone
          if (event && event.timezoneName) {
            eventTimezoneName = event.timezoneName;
            data.eventTimezoneName = eventTimezoneName;
          }

          // Set recap definition version & updated_at
          if (data.recap_definition && data.recap_definition.version) {
            data.events_recaps_version = data.recap_definition.version;
            data.events_recaps_updated_at = data.recap_definition.updated_at;
          }

          /**
           * Given a form ID and a list of form response data, create a FormResponseSet
           */
          let loadFormResponseSet = (formId, formResponseDatas) => {
            let form;
            return this.$q.when(this.FormService.getById(formId))
              .then(loadedForm => {
                form = loadedForm;
                if (form) {
                  // Load form response set
                  const formResponseSetData = {
                    form,
                    user_role_id,
                    current_location,
                    events_custom_forms_id: form.id,
                    events_custom_responses_id: recapResponseId,
                    form_responses: formResponseDatas,
                    eventTimezoneName,
                    recapDefinitionId,
                  };
                  return this.FormResponseSetService.create(formResponseSetData)
                    .catch(err => {
                      this.LogService.warn(`RecapResponseService.create() swallowing error creating FormResponseSet. error:`, err, 'data:', formResponseSetData);
                      return null;
                    });
                } else {
                  return this.$q.reject(createError({
                    error: 'MissingForm',
                    message: `Unable to load form ${formId} for form response set on recap definition ${recapDefinitionId}`,
                  }));
                }
              })
              .then(formResponseSet => {
                return formResponseSet;
              });
          };

          const formResponseDatasByFormId = this._groupFormResponseDatasByFormId(data.form_responses || []);
          const formResponseSetPromises = Object.values(formResponseDatasByFormId)
            .filter(formResponseDatas => formResponseDatas.length > 0)
            .map(formResponseDatas => {
              let formId = formResponseDatas[0].events_custom_forms_id;
              return loadFormResponseSet(formId, formResponseDatas)
                .catch(err => {
                  this.LogService.warn(`RecapResponseService.create() swallowing error creating FormResponseSet for form ${formId} for RecapResponse ${recapResponseId} on recap definition ${recapDefinitionId}:`, err);
                  return null;
                })
            });

          return this.$q.all(formResponseSetPromises)
            .then(formResponseSets => {
              data.form_response_sets = formResponseSets.filter(formResponseSet => !!formResponseSet);
            });
        })
        .then(() => {
          // Load field responses
          let fieldResponseDatas = data.field_responses || [];
          return this.$q
            .all(fieldResponseDatas.map(fieldResponseData => {
                return this.$q.when(this.FieldResponseService.create({
                  ...fieldResponseData,
                  user_role_id,
                  current_location,
                  recapDefinitionId,
                }))
                  .catch(err => {
                    this.LogService.warn(`RecapResponseService.create() Swallowing error creating FieldResponse ${fieldResponseData.id} for RecapResponse ${recapResponseId} on recap definition ${recapDefinitionId}:`, err, "field response data:", fieldResponseData);
                    return null;
                  });
              })
            )
            .then(fieldResponses => {
              data.field_responses = fieldResponses.filter(fieldResponse => fieldResponse !== null);
            });
        })
    );

    return this.$q.all(promises)
      .then(() => {
        const recapResponse = new RecapResponse(data);

        // Persist to form sets and fields
        for (let formResponseSet of recapResponse.form_response_sets) {
          formResponseSet.recapResponse = recapResponse;
        }
        for (let fieldResponse of recapResponse.field_responses) {
          fieldResponse.recapResponse = recapResponse;
        }

        //pass through flash messages
        if(flashMessages && flashMessages.length) {
          recapResponse.flashMessages = flashMessages;
        }
        return recapResponse;
      });
  }

  /**
   * When saving a recap to the API, the ID may change due to recap versioning.
   *
   * We need to update all references to the old recap ID and data to now point to the new one.
   *
   * This may include:
   *
   * - DataSyncService local promise by ID for getById
   * - DataSyncService local promise for getMultiple
   * - Positions with an embedded position.recapResponse
   * - Future (after v1.10): IncrementalDataStore references to position or recap response
   *
   * @param staffingUser
   * @param newRecapResponse
   * @param oldRecapResponseId
   */
  updateRecapResponseReferencesAfterSave(staffingUser, newRecapResponse, oldRecapResponseId) {
    const promises = [];
    oldRecapResponseId = normalizeIdentifier(oldRecapResponseId);
    const newRecapResponseId = normalizeIdentifier(newRecapResponse.id);

    // First update DataSyncService local object/promise map for both old and new ID

    let recapResponseIdsToUpdate = [newRecapResponseId];
    if (oldRecapResponseId !== newRecapResponseId) {
      recapResponseIdsToUpdate.push(oldRecapResponseId);
    }
    let setLocalObjectPromises = recapResponseIdsToUpdate.map(
      recapResponseId => this.DataSyncService.setObjectById(type, recapResponseId, newRecapResponse)
    );
    promises.push(this.$q.all(setLocalObjectPromises));


    // Next update DataSyncService local getMultiple object/promise map for both old and new ID
    const getMultiplePathsToUpdate = this._usedPathsForGetMultiple;
    const setLocalMultiObjectPromises = recapResponseIdsToUpdate.map(
      recapResponseId => this.$q.all(getMultiplePathsToUpdate.map(path => {
        this.LogService.debug(`RecapResponseService.updateRecapResponseReferencesAfterSave() updating getMultiple promise for path ${path}, recap response id ${recapResponseId}`);
        return this.DataSyncService.setObjectByIdForGetMultiple(type, path, recapResponseId, newRecapResponse);
      }))
    );
    promises.push(this.$q.all(setLocalMultiObjectPromises));


    // Check if there is an existing position that references the old or new recap response ID, and update the
    // recap response

    if (newRecapResponse.position) {
      // Update recap response for this position (not sure if this actually does anything useful)
      newRecapResponse.position.recapResponse = newRecapResponse;

      // More importantly, take the position ID and attempt to retrieve a local object for it, and update that reference
      const positionId = normalizeIdentifier(newRecapResponse.position.id);
      promises.push(
        this.PositionService.getLocalById(positionId)
          .then(position => {
            position.recapResponse = newRecapResponse;
          })
      )
    }

    // Use PositionService to search for references to this recap response
    if (staffingUser) {
      const eventId = normalizeIdentifier(newRecapResponse.eventId);
      promises.push(
        this.PositionService.updateRecapResponseReferencesForEvent(staffingUser, eventId, newRecapResponse, oldRecapResponseId)
      );
    }

    return this.$q.all(promises);
  }

  getById(id, config = null) {
    const loggedInStaffingUser = this.DataSyncService.staffUser;
    if (loggedInStaffingUser) {
      // Load recap response via incremental data store
      const staffUserId = normalizeIdentifier(loggedInStaffingUser.id);
      const dataStore = this.getIncrementalDataStoreForStaffingUser(staffUserId);
      return dataStore.getById(id, config)
        .catch(err => {
          this.LogService.error("Error retrieving recap response set:", err);
          return this.$q.reject(err);
        });
    } else {
      // Load recap response via individual API endpoint
      const factory = data => this.create(data, config);
      return this.DataSyncService
        .getObjectById(type, getRecapResponseSetUrl, id, factory, config)
        .catch(err => {
          this.LogService.error("Error retrieving recap response set:", err);
          return this.$q.reject(err);
        })
    }
  }

  getLocalById(id) {
    return this.$q.when(this._mostRecentDataStore)
      .then(dataStore => {
        if (!dataStore) {
          return null;
        }
        return dataStore.getLocalById(id);
      });
  }

  getIncrementalDataStoreForStaffingUser(staffingUser) {
    const staffingUsersId = normalizeIdentifier(staffingUser.id);
    const syncWindowDefinition = staffingUser.syncWindowDefinition;
    const dataStoreTypeKey = `RecapResponse:${staffingUsersId}:${syncWindowDefinition.name}`;
    if (this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[dataStoreTypeKey] === undefined) {
      const factory = (data, ...args) => {
        this.LogService.debug(`RecapResponseService factory called with data`, data, `args`, args);
        return this.create(data, ...args);
      };
      const basePath = getMyRecapResponseSetsUrl
        .replace('{{staffing_users_id}}', staffingUsersId);
      const path = this.APIService.addURLParamsFromObject(basePath, syncWindowDefinition.params);
      if (!this._usedPathsForGetMultiple.includes(path)) {
        this._usedPathsForGetMultiple.push(path);
      }
      const dataStore = this.IncrementalDataStoreService.getOrBuild(dataStoreTypeKey, path, factory);
      dataStore.postUpdateCallback = (updatedObjects, store) => this._postIncrementalDataStoreUpdateRemoveDuplicates(updatedObjects, store, staffingUser);
      this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[dataStoreTypeKey] = dataStore;

      if (INCREMENTAL_PURGE_OLD_DATA_STORE) {
        const destroyPromises = Object.keys(this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName)
          .filter(currKey => currKey !== dataStoreTypeKey)
          .map(keyToDelete => {
            this.LogService.debug(`RecapResponseService purging previous data store: ${keyToDelete}`);
            delete this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[keyToDelete];
            return this.$q.when(this.IncrementalDataStoreService.destroy(keyToDelete));
          });
        if (destroyPromises.length) {
          this.$q.all(destroyPromises)
            .then(() => {
              this.LogService.debug(`RecapResponseService purged previous data stores`);
            });
        }
      }
    }
    this._mostRecentDataStore = this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[dataStoreTypeKey];
    return this.$q.when(this._incrementalDataStoreByStaffingUsersIdAndSyncWindowName[dataStoreTypeKey]);
  }

  /**
   * Find any duplicate responses and remove the earlier ones.
   * Duplicates are recap responses that have the same root ID.
   */
  _postIncrementalDataStoreUpdateRemoveDuplicates(updatedObjects, store, staffingUser) {
    return store.getAll()
      .then(allObjects => {
        const responseIdsByRootId = {};
        const responsesById = {};
        const responseIdPairsToRemoveAndReplace = [];
        const removeAndReplacePromises = [];

        // First create mapping of root ID to response ID's
        allObjects.forEach(response => {
          const rootId = normalizeIdentifier(response.root_id);
          const responseId = normalizeIdentifier(response.id);
          if (!rootId) {
            this.LogService.warn(`RecapResponseService.getIncrementalDataStoreForStaffingUser() postUpdateRemoveDuplicateResponses() no root_id for response id ${response && response.id}`);
            return;
          }
          if (!(rootId in responseIdsByRootId)) {
            responseIdsByRootId[rootId] = [];
          }
          responseIdsByRootId[rootId].push(responseId);
          responsesById[responseId] = response;
        });

        // Look for root ID's with multiple response ID's
        // We want to record the most recent response ID, and all others we'll delete and replace
        Object.keys(responseIdsByRootId).forEach(rootId => {
          const responseIds = responseIdsByRootId[rootId];
          if (responseIds.length > 1) {
            // Found multiple response ID's. Sort by ID
            responseIds.sort().reverse();
            const keepResponseId = responseIds[0];
            for (let idx = 1; idx < responseIds.length; idx++) {
              responseIdPairsToRemoveAndReplace.push([responseIds[idx], keepResponseId]);
            }
          }
        });
        // Finally, remove and replace responses as necessary
        responseIdPairsToRemoveAndReplace.forEach(pair => {
          const [removeResponseId, keepResponseId] = pair;
          const keepResponse = responsesById[keepResponseId];
          this.LogService.debug(`RecapResponseService postUpdateRemoveDuplicateResponses() replacing old response ${removeResponseId} with new response ${keepResponseId}`);
          removeAndReplacePromises.push(this.$q.when(
            this.PositionService.updateRecapResponseReferencesForEvent(staffingUser, keepResponse.eventId, keepResponse, removeResponseId)
          ));
          removeAndReplacePromises.push(this.$q.when(
            store.removeById(removeResponseId)
          ))
        });
        return this.$q.all(removeAndReplacePromises);
      });
  }

  getAllForStaffUser(staffingUser, config = null) {
    return this.$q.when(this.getIncrementalDataStoreForStaffingUser(staffingUser))
      .then(dataStore => dataStore.getAll(config))
      .catch(response => {
        this.LogService.error("Error retrieving recap responses (RecapResponseService.getAllForStaffUser):", response);
        return this.$q.reject(createError({
          error: 'ErrorRetrievingStaffingUserRecapResponseSets',
          message: 'Error retrieving recap response sets from server',
          response
        }));
      });
  }

  getOrCreateForPosition(position, config = null) {
    return this.getForPosition(position, config)
      .then(response => response || this.createForPosition(position));
  }

  /**
   * Create a Recap Response with pre-loaded data as pulled for a partial response
   * @param position
   * @param data
   * @returns RecapResponse
   */
  createFromDataForPosition(position, data) {
    this.LogService.warn(`RecapResponseService.createFromDataForPosition called and may be broken. position:`, position, `data:`, data);

    let event = position.event;
    let recapDefinitionId = event.events_recaps_id;
    if (!recapDefinitionId) {
      return this.$q.reject(createError({
        error: 'NoRecapDefinitionSpecifiedForEvent',
        message: 'This event does not have a recap definition specified',
      }));
    }
    let zdata = {
      staffing_users_id: position.staffing_users_id,
      events_id: event.id,
      events_recaps_id: recapDefinitionId,
      position,
    };
    return this.create(data);
  }

  getStaffUserById(staffingUserId, config = null) {
    return this.StaffUserService.getById(staffingUserId, config);
  }

  getForPosition(position, config = null) {
    let event = position.event;
    let staffUserId = position.staffing_users_id;
    if (!event) {
      return this.$q.reject(createError({
        error: 'MissingEventForPosition',
        message: 'Position does not have an event',
      }));
    }
    if (!staffUserId) {
      return this.$q.reject(createError({
        error: 'NoStaffingUserSpecifiedForPosition',
        message: 'Position is not assigned to a staffing user',
      }));
    }

    let myConfig = config ? {...config} :  {};
    if (position.is_guerrilla) {
      myConfig.force = true;
    }
    return this.getStaffUserById(staffUserId, myConfig)
      .then(staffUser => this.getAllForStaffUser(staffUser, myConfig))
      .then(responses => {
        const recapResponsesMatchingEvent = responses.filter(response => response.events_id == event.id);
        let found = null;
        if (recapResponsesMatchingEvent.length > 1) {
          const compareRecapResponsesById = (recapResponseA, recapResponseB) => {
            const idA = normalizeIdentifier(recapResponseA);
            const idB = normalizeIdentifier(recapResponseB);
            if (idA < idB) return -1;
            else if (idA > idB) return 1;
            else return 0;
          };
          let sortedRecapResponsesMatchingEvent = recapResponsesMatchingEvent
            .sort(compareRecapResponsesById)
            .reverse();
          found = sortedRecapResponsesMatchingEvent[0];
          this.LogService.warn(`RecapResponseService.getForPosition(position.id=${position.id}) found multiple responses! Selecting highest ID`, recapResponsesMatchingEvent, 'found:', found);
        } else if (recapResponsesMatchingEvent.length === 1) {
          found = recapResponsesMatchingEvent[0];
        } else {
          found = null;
        }
        if (found) {
          found.position = position;
        }
        this.LogService.debug(`RecapResponseService.getForPosition(position.id=${position.id}) returning:`, found);
        return found;
      });
  }

  createForPosition(position) {
    let event = position.event;
    let recapDefinitionId = event.events_recaps_id;
    if (!recapDefinitionId) {
      return this.$q.reject(createError({
        error: 'NoRecapDefinitionSpecifiedForEvent',
        message: 'This event does not have a recap definition specified',
      }));
    }
    let data = {
      staffing_users_id: position.staffing_users_id,
      events_id: event.id,
      events_recaps_id: recapDefinitionId,
      position,
    };
    return this.create(data);
  }

  save(recapResponse) {
    return this.$q.all([
      this._uploadAllPendingDocumentsForRecapResponse(recapResponse),
      this._deleteAllPendingDocumentsForRecapResponse(recapResponse),
    ])
      .then(async () => {
        // Copy recap definition version and updated_at if available
        if (recapResponse.recap_definition && recapResponse.recap_definition.version) {
          recapResponse.events_recaps_version = recapResponse.recap_definition.version;
          recapResponse.events_recaps_updated_at = recapResponse.recap_definition.updated_at;
        }

        let data = this.serialize(recapResponse);
        return this.APIService.post(getRecapResponseSetUrl, data)
      })
      .catch(response => {
        return this.$q.reject(createError({
          error: 'ErrorSavingRecapResponse',
          message: 'There was an error saving your response',
          response,
        }));
      })
      .then(async (response) => {
        // Handle an error saving the recap response if applicable
        const errors = getApiError(response);
        if (errors) {
          return this.$q.reject(errors);
        }

        this.LogService.debug(`Successfully saved recap response; server returned:`, response);
        return this.create(response.data)
          .catch(err => {
            this.LogService.warn("Error creating recap response after saving:", err);
            return this.$q.reject(err);
          });
      });
  }

  _uploadAllPendingDocumentsForRecapResponse(recapResponse) {
    const uploadPromises = recapResponse.collectAllVisibleFieldResponses()
      .filter(fieldResponse => fieldResponse.documentsToUpload.length > 0)
      .map(fieldResponse => {
        return fieldResponse.documentsToUpload.map(document => this._uploadDocument(document, fieldResponse, recapResponse));
      })
      .reduce((pre, cur) => pre.concat(cur), []);
    return this.$q.all(uploadPromises);
  }

  _uploadDocument(document, fieldResponse, recapResponse) {
    const uploadPath = uploadRecapResponseFile;
    const params = {
      events_custom_id: fieldResponse && fieldResponse.events_custom_id || null,
      events_id: recapResponse.eventId || null,
    };
    let uploadUrl = this.APIService.convertPathToURL(uploadPath, params);

    return this.DocumentService.upload(document, uploadUrl, null, 'file')
      .then(result => {
        this.LogService.log("Uploaded document! Result:", result);
        let data = result.data;
        fieldResponse.documentWasUploaded(document, data.id);
      });
  }

  /**
   * Used so we can externally confirm and remove documents to be uploaded
   * from our field responses.
   */
  documentWasUploaded(recapResponse, fieldResponseId, document, newId) {
    recapResponse
      .collectAllVisibleFieldResponses()
      .find(fieldResponse => fieldResponse.id == fieldResponseId)
      ?.documentWasUploaded(document, newId);
  }

  _deleteAllPendingDocumentsForRecapResponse(recapResponse) {
    // TODO: Implement this
    return this.$q.when([]);
  }

  serialize(recapResponse) {
    let data = {
      id: recapResponse.id,
      staffing_users_id: recapResponse.staffing_users_id,
      users_id: recapResponse.users_id,
      started_at: this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(recapResponse.started_at),
      started_at_tz: this.DateTimeService.serializeDateTimeToIsoWithOffset(recapResponse.started_at, ''),
      ended_at: this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(new Date()),
      ended_at_tz: this.DateTimeService.serializeDateTimeToIsoWithOffset(new Date(), ''),
      start_lat: recapResponse.coords?.startCoordinates?.lat,
      start_lon: recapResponse.coords?.startCoordinates?.lon,
      end_lat: recapResponse.coords?.endCoordinates?.lat,
      end_lon: recapResponse.coords?.endCoordinates?.lon,
      events_id: recapResponse.events_id,
      events_recaps_id: recapResponse.events_recaps_id,
      events_recaps_external_links_views_id: recapResponse.events_recaps_external_links_views_id,
      events_recaps_version: recapResponse.events_recaps_version,
      events_recaps_updated_at: this.DateTimeService.serializeDateTimeForAPIWithTimezoneOrServerTimezone(recapResponse.events_recaps_updated_at),
      staff_app_version: this.AppVersionService.appVersion,
      staff_app_version_full: this.AppVersionService.fullAppVersionInfo,
      last_staff_app_data_sync_uuid: this.DataSyncService.lastDataSyncUUID,
      status_id: recapResponse.status_id,
      form_responses: recapResponse.collectVisibleFormResponses()
        .map(formResponse => this.FormResponseService.serialize(formResponse)),
    };

    data._submission_diagnostics = this.getBasicDiagnosticInfoForRecapResponse(recapResponse);
    return data;
  }

  /**
   * Create form response sets by grouping form responses by form ID
   */
  _groupFormResponseDatasByFormId(formResponseDatas) {
    return formResponseDatas
      .reduce((formResponseDatasByFormId, formResponseData) => {
        let formId = formResponseData.events_custom_forms_id;
        if (!formId) {
          this.LogService.error("Form Response is missing form ID:", JSON.stringify(formResponseData));
          throw createError({
            error: 'MissingFormIdForFormResponse',
            message: 'Form ID is required for form response',
            formResponseData,
          });
        }
        if (!(formId in formResponseDatasByFormId)) {
          formResponseDatasByFormId[formId] = [];
        }
        formResponseDatasByFormId[formId].push(formResponseData);
        return formResponseDatasByFormId;
      }, {});
  }

  _prepareLocationVariablesForDiagnostics(locationVariables) {
    const objects = locationVariables || [];
    const ids = objects.map(lv => normalizeIdentifier(lv.id));
    ids.sort();
    return {
      objects,
      ids,
    };
  }

  getBasicDiagnosticInfoForRecapResponse(recapResponse) {
    const event = recapResponse && recapResponse.event;
    const eventLVInfo = this._prepareLocationVariablesForDiagnostics(event && event.location_variables);
    const previewLVInfo = this._prepareLocationVariablesForDiagnostics(recapResponse && recapResponse.preview_location_variables);
    const recapDefinition = recapResponse && recapResponse.recapDefinition;
    const definitionLVInfo = this._prepareLocationVariablesForDiagnostics(recapDefinition && recapDefinition.location_variables);
    return {
      event_location_variables: eventLVInfo.objects,
      event_location_variable_ids: eventLVInfo.ids,
      preview_location_variables: previewLVInfo.objects,
      preview_location_variable_ids: previewLVInfo.ids,
      definition_location_variables: definitionLVInfo.objects,
      definition_location_variable_ids: definitionLVInfo.ids,
    };
  }

  getFullDiagnosticInfoForRecapResponse(recapResponse) {
    const event = recapResponse && recapResponse.event;
    const position = recapResponse && recapResponse.position;
    const recapDefinition = recapResponse && recapResponse.recapDefinition;
    return {
      ...this.getBasicDiagnosticInfoForRecapResponse(recapResponse),
      definition_id: recapDefinition && recapDefinition.id,
      definition_version: recapDefinition && recapDefinition.version,
      definition_updated_at: recapDefinition && recapDefinition.updated_at,
      events_id: event && event.id,
      venues_id: event && event.venues_id,
      position_id: position && position.id,
      staffing_users_id: recapResponse && recapResponse.staffing_users_id,
    };
  }
}

export default RecapResponseService;

m.service('RecapResponseService', RecapResponseService);
